"use client";

import { useState, useEffect, useCallback, useRef } from "react";

interface UseAIInsightsOptions {
  delay?: number;
  prompt?: string;
}

export function useAIInsights({ delay = 3000, prompt }: UseAIInsightsOptions = {}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Widget slides into view after delay
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  // Escape key collapses card
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isExpanded) setIsExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]);

  const handleGetInsights = useCallback(() => {
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const finalPrompt =
      prompt ??
      `Analyze this page and provide a structured summary focused on business outcomes.\n\nPage URL:\n${pageUrl}`;
    window.open(`https://chatgpt.com/?q=${encodeURIComponent(finalPrompt)}`, "_blank");
  }, [prompt]);

  // Toggle on click/tap (mobile)
  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Hover enter: cancel any pending close, expand
  const handleHoverEnter = useCallback(() => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setIsExpanded(true);
  }, []);

  // Hover leave: small debounce to prevent flicker
  const handleHoverLeave = useCallback(() => {
    leaveTimer.current = setTimeout(() => setIsExpanded(false), 200);
  }, []);

  return { isVisible, isExpanded, handleGetInsights, handleToggle, handleHoverEnter, handleHoverLeave };
}
